import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageHeader, Card, KpiCard, EmptyState, ErrorState, Button,
  IconButton, JalaliDatePicker,
} from '@parsina/framework';
import { CalendarDays, ChevronDown, ChevronRight, Info } from 'lucide-react';
import { C, CT } from '../../constants/theme';
import { useTranslation, MIRROR_ICON } from '../../i18n/I18nProvider.jsx';
import { useAppNavigate } from '../../hooks/useAppNavigate.js';
import { useClinicScope } from '../../hooks/useClinicScope.jsx';
import { useToday } from '../../hooks/useToday.js';
import { useNextService } from '../../hooks/useNextService.js';
import { buildDayModel, dayCounts } from '../../lib/appointments.js';
import { todayInIran, shiftISO, isoDayToMs, nowMinutesInIran, ISO_DATE_RE } from '../../lib/iranTime.js';
import AppointmentRow from '../../components/AppointmentRow.jsx';
import NextPatientHero from '../../components/NextPatientHero.jsx';
import { TodaySkeletonWeb } from './Today.skeleton.jsx';

// امروز — the screen that makes this app real.
//
// Every row here is an `appointments` row the clinic's منشی created. Nothing on
// this screen writes: the day is read, and the read is the whole point
// (DESIGN.md §6 — "the phase where the dentist app stops being a shell and
// starts showing the clinic's appointments").

// A titled section of the day with a count pill. Collapsible for «انجام‌شده»,
// which is history rather than work.
const Block = ({ title, count, tint, tintText, collapsible, defaultOpen = true, children }) => {
  const [open, setOpen] = React.useState(defaultOpen);
  // The disclosure has to NAME the region it controls, or a screen reader
  // announces "expanded" with nothing attached to it.
  const bodyId = React.useId();
  return (
    <Card style={{ padding: 0, marginBottom: 14, overflow: 'hidden' }}>
      {/* The whole strip stays clickable for the mouse, but the ACCESSIBLE
          disclosure is the button inside the heading — the standard accordion
          shape. The previous form put role="button" on this container with the
          title inside it, and a role=button's children are presentational: the
          section heading was erased, and «انجام‌شده» could not be reached from a
          screen reader's heading list. */}
      <div
        onClick={collapsible ? () => setOpen((o) => !o) : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '14px 20px',
          borderBottom: open ? `1px solid ${C.border}` : 'none',
          cursor: collapsible ? 'pointer' : 'default',
        }}
      >
        <h2 style={{ margin: 0, fontSize: 14, fontWeight: 800, color: C.textPrimary }}>
          {collapsible ? (
            <button
              type="button"
              aria-expanded={open}
              aria-controls={bodyId}
              onClick={(e) => {
                // The strip's own handler would otherwise toggle a second time
                // and cancel this one out.
                e.stopPropagation();
                setOpen((o) => !o);
              }}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                margin: 0,
                font: 'inherit',
                color: 'inherit',
                cursor: 'pointer',
              }}
            >
              {title}
            </button>
          ) : (
            title
          )}
        </h2>
        <span
          style={{
            fontSize: 11,
            fontWeight: 800,
            borderRadius: 20,
            padding: '2px 9px',
            background: tint || CT.surface2,
            color: tintText || C.textSecondary,
          }}
        >
          {count}
        </span>
        {collapsible && (
          // A lucide glyph, not a «▲»/«▼» text character: the arrow glyphs are
          // font-dependent and sit on the text baseline, so they drift out of
          // line with the row in Vazirmatn.
          <ChevronDown
            size={16}
            color={C.textMuted}
            aria-hidden
            style={{
              marginInlineStart: 'auto',
              flexShrink: 0,
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform .15s',
            }}
          />
        )}
      </div>
      {open && <div id={bodyId} style={{ padding: '12px 16px 4px' }}>{children}</div>}
    </Card>
  );
};

// Previous/next day.
//
// The kit's IconButton owns the affordance — label, tooltip, hit target — so
// this is not a hand-rolled button. What is app-specific is the chevron's
// MEANING: «روز قبل» points back the way the reader came from, which is RIGHT
// in Persian and LEFT in English. `MIRROR_ICON` (scaleX(--dir-flip); +1 in RTL,
// -1 in LTR) turns lucide's right-pointing glyph into exactly that, and `next`
// is its negation. Rendering the SAME glyph for both — which a fixed
// left-chevron does in RTL — leaves the two day buttons indistinguishable in
// the product language.
const NEXT_ICON = { transform: 'scaleX(calc(var(--dir-flip, 1) * -1))' };

const NavBtn = ({ label, onClick, next }) => (
  <IconButton
    variant="outline"
    size={40}
    label={label}
    onClick={onClick}
    icon={<ChevronRight size={16} aria-hidden style={next ? NEXT_ICON : MIRROR_ICON} />}
    style={{ borderRadius: 10 }}
  />
);

const TodayScreen = () => {
  const { t, formatNumber, formatDate } = useTranslation();
  const navigate = useAppNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeId } = useClinicScope();

  // The selected day lives in the URL, so a refresh, a back button and a shared
  // link all restore the day being read. Today is the default and is omitted.
  const rawDate = searchParams.get('date');
  const today = todayInIran();
  const selected = ISO_DATE_RE.test(rawDate || '') ? rawDate : today;
  const isToday = selected === today;

  const setDay = React.useCallback(
    (iso) => {
      const next = new URLSearchParams(searchParams);
      if (!iso || iso === todayInIran()) next.delete('date');
      else next.set('date', iso);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const { appointments, queue, loading, error, reload } = useToday({
    date: selected,
    clinicId: activeId,
  });

  // Wait and chair clocks are minute-grained, so the screen re-renders on a
  // slow tick rather than making every row own a timer.
  const [tick, setTick] = React.useState(() => Date.now());
  React.useEffect(() => {
    const timer = setInterval(() => setTick(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);

  const model = React.useMemo(
    () =>
      buildDayModel({
        appointments,
        queue,
        isToday,
        nowMin: nowMinutesInIran(tick) ?? 0,
      }),
    [appointments, queue, isToday, tick],
  );
  // `counted`, not `dayApts`: it is the day sheet PLUS anyone in the room who
  // is not on it (a row with no clinic_id, or one marked arrived on an earlier
  // day and never completed). dayCounts' own contract is that the numbers are
  // derived from the same rows that render underneath them, and the waiting
  // room renders.
  const counts = React.useMemo(() => dayCounts(model.counted), [model.counted]);

  // What the next patient is coming FOR. Read beside the day, on the PATIENT
  // changing rather than on the day poll — see hooks/useNextService.js.
  const nextService = useNextService({
    patientId: model.nextApt?.patientId || null,
    clinicId: activeId,
  });

  // What the screen actually has to show. The day sheet is not the whole of it:
  // gating on `dayApts` alone painted «هیچ نوبتی برای این روز ثبت نشده» over a
  // waiting room with patients in it — the day read succeeded and said nothing,
  // which is the one failure this screen cannot afford.
  const hasRows = model.counted.length > 0;

  const openPatient = React.useCallback(
    (apt) => {
      if (apt?.patientId) navigate('patient', { id: apt.patientId });
    },
    [navigate],
  );

  // جلسهٔ درمان — THIS appointment's workspace (docs/dent-visit.md). Keyed on
  // the appointment rather than the patient, because the appointment is the
  // visit. A «ثبت سریع» walk-in has no patient record, and the workspace cannot
  // open on one, so its row offers no visit button at all.
  const openVisit = React.useCallback(
    (apt) => {
      if (apt?.id && apt?.patientId) navigate('visit', { appointmentId: apt.id });
    },
    [navigate],
  );

  const kpis = [
    { key: 'total', label: t('today.counts.total'), value: counts.total },
    { key: 'here', label: t('today.counts.here'), value: counts.here, accent: C.teal },
    { key: 'scheduled', label: t('today.counts.scheduled'), value: counts.scheduled },
    { key: 'completed', label: t('today.counts.completed'), value: counts.completed },
    // Only when there are any: a permanent «۰ لغو شده» tile is a number that
    // never says anything.
    ...(counts.cancelled > 0
      ? [{ key: 'cancelled', label: t('today.counts.cancelled'), value: counts.cancelled }]
      : []),
  ];

  const dateNav = (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <NavBtn label={t('today.date.prev')} onClick={() => setDay(shiftISO(selected, -1))} />
      <div style={{ width: 210 }}>
        <JalaliDatePicker value={selected} onChange={(v) => setDay(v || today)} height={40} />
      </div>
      <NavBtn label={t('today.date.next')} onClick={() => setDay(shiftISO(selected, 1))} next />
      {!isToday && (
        <Button variant="subtle" onClick={() => setDay(today)}>
          {t('today.date.today')}
        </Button>
      )}
    </div>
  );

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflow: 'hidden' }}>
      {/* The header stays mounted through the error branch on purpose: the date
          navigator remains operable, so a failed day is one click from a good one. */}
      <PageHeader
        title={t('today.title')}
        sub={formatDate(isoDayToMs(selected), 'full')}
      >
        {dateNav}
      </PageHeader>

      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '24px 32px 40px' }}>
        {error ? (
          // role="alert" so a failed day is ANNOUNCED, not just drawn: the read
          // is asynchronous and the dentist's focus is on the date navigator
          // above, which stays operable through the failure.
          //
          // On the WRAPPER, not on <Card>: the kit destructures `role` and only
          // applies it to its onClick (button) form, so a role passed to a plain
          // Card is silently dropped.
          <div role="alert">
            <Card>
              <ErrorState
                title={t('today.error.title')}
                description={t('errors.load.today')}
                onRetry={reload}
                retryLabel={t('common.actions.retry')}
              />
            </Card>
          </div>
        ) : (
          <div
            data-testid="today-content"
            data-state={loading ? 'loading' : hasRows ? 'content' : 'empty'}
            aria-busy={loading || undefined}
          >
            <div style={{ display: 'flex', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
              {kpis.map((k) => (
                <KpiCard
                  key={k.key}
                  label={k.label}
                  value={formatNumber(k.value)}
                  accent={k.accent}
                  loading={loading}
                />
              ))}
            </div>

            {/* The day's OWN shape while it loads — the hero and the first
                blocks of rows at their real size — never a generic card stack
                or a spinner (DESIGN.md §"Loading & skeleton"). The header, the
                date navigator and the KPI tiles above are real chrome. */}
            {loading && <TodaySkeletonWeb label={t('common.loading')} />}

            {!loading && !hasRows && (
              <Card>
                <EmptyState
                  icon={<CalendarDays size={48} color={C.textMuted} />}
                  title={t('today.empty.title')}
                  description={t('today.empty.description')}
                />
              </Card>
            )}

            {!loading && hasRows && (
              <>
                {/* The hero owns its own digits/name formatting — the old Alert
                    interpolated them here and once shipped Latin digits into an
                    otherwise-Persian screen. */}
                <NextPatientHero
                  apt={model.nextApt}
                  service={nextService}
                  onOpen={openPatient}
                  onOpenVisit={openVisit}
                />

                {model.roomQueue.length > 0 && (
                  <Block
                    title={t('today.blocks.queue')}
                    count={formatNumber(model.roomQueue.length)}
                    tint={CT.accentSoft}
                    tintText={CT.accentInk}
                  >
                    {model.roomQueue.map((apt) => (
                      <AppointmentRow
                        key={apt.id}
                        apt={apt}
                        queuePos={model.queuePosOf.get(apt.id) ?? null}
                        nowMs={tick}
                        onOpen={openPatient}
                        onOpenVisit={openVisit}
                      />
                    ))}
                  </Block>
                )}

                {model.upcoming.length > 0 && (
                  <Block title={t('today.blocks.upcoming')} count={formatNumber(model.upcoming.length)}>
                    {model.upcoming.map((apt) => (
                      <AppointmentRow
                        key={apt.id}
                        apt={apt}
                        nowMs={tick}
                        onOpen={openPatient}
                        onOpenVisit={openVisit}
                      />
                    ))}
                  </Block>
                )}

                {model.done.length > 0 && (
                  <Block
                    title={t('today.blocks.done')}
                    count={formatNumber(model.done.length)}
                    collapsible
                    defaultOpen={model.roomQueue.length === 0 && model.upcoming.length === 0}
                  >
                    {model.done.map((apt) => (
                      <AppointmentRow
                        key={apt.id}
                        apt={apt}
                        nowMs={tick}
                        onOpen={openPatient}
                        onOpenVisit={openVisit}
                      />
                    ))}
                  </Block>
                )}
              </>
            )}

            {/* Says out loud what this screen does NOT do, so its missing
                buttons read as a boundary rather than as an omission. */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                marginTop: 18,
                color: C.textMuted,
                fontSize: 12,
              }}
            >
              <Info size={14} />
              <span>{t('today.readOnlyNote')}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TodayScreen;
